import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";

import {
  MenuItem,
  SplitButton,
  Button,
  mergeClasses,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Body1,
  ProgressBar,
  Image,
  Spinner,
  PopoverTrigger,
  PopoverSurface,
  Popover,
  Tooltip
} from "@fluentui/react-components";
import {
  MicFilled,
  Speaker220Filled,
  Settings20Filled,
  ChevronUpFilled,
  MicOffFilled,
  Dismiss24Regular,
  InfoRegular
} from "@fluentui/react-icons";
import configManager from "../../singleton/ConfigManager";
import { useMicrophoneStatus } from "../room/useMicrophoneStatus";
import { useCan } from "../room/useCan";
import { useMicrophone } from "../room/useMicrophone";
import { PermissionStatus } from "../../utils/media-devices-utils";
import PermissionDenied from "../../assets/images/PermissionDenied.png";
import { MicPopoverSelect } from "../fluent/MicPopoverSelect";
import { useVolumeMeter } from "../misc/useVolumeMeter";
import { useSpeakers } from "../room/useSpeakers";
import { useSound } from "../room/useSound";
import { SOUND_SPEAKER_TONE } from "../../systems/sound-effects-system";
import { useSettingsContext } from "../room/SettingsContext";
import { RoomResourceIds } from "../../utils/room-resource-state-utils";
import { resolveMediaActionState } from "../room/media-resource-state-utils";
import { useLocalMediaResourceAvailability } from "../room/useLocalMediaResourceAvailability";
import { useUnavailableToolbarAction } from "./useUnavailableToolbarAction";
import { isWebRtcDisabled } from "../../utils/room-entry-admission";

export default function ToolbarMic({ styles, scene }) {
  const { micDeviceChanged, micDevices } = useMicrophone(scene);
  const { speakerDeviceChanged, speakerDevices } = useSpeakers();

  const { isMicMuted, toggleMute, prepareMic, permissionStatus, isMicEnabled, isMicStarting } =
    useMicrophoneStatus(scene);
  const micPermissionDenied = permissionStatus === PermissionStatus.DENIED;
  const userCanVoiceChat = useCan("voice_chat");
  const canVoiceChat = !isWebRtcDisabled() && userCanVoiceChat && configManager.config.voiceChat.enabled;
  const isMicActive = isMicEnabled && !isMicMuted;
  const { unavailableReason: microphoneUnavailableReason } = useLocalMediaResourceAvailability(
    RoomResourceIds.MICROPHONE,
    {
      label: "Microphone",
      locallyOwned: isMicActive
    }
  );
  const microphoneActionState = resolveMediaActionState({
    permissionDenied: micPermissionDenied,
    locallyOwned: isMicActive,
    active: isMicActive,
    unsupported: !canVoiceChat,
    unavailableReason: microphoneUnavailableReason
  });
  const micUnavailableReason =
    !micPermissionDenied && microphoneActionState.blocked
      ? microphoneActionState.unavailableReason || "Microphone is unavailable"
      : null;
  const microphoneIcon = useMemo(
    () =>
      isMicMuted || !canVoiceChat || micPermissionDenied || !isMicEnabled ? (
        <MicOffFilled
          style={{
            width: "24px",
            height: "24px",
            color:
              micPermissionDenied || !canVoiceChat || isMicMuted || !isMicEnabled || micUnavailableReason
                ? "var(--color-danger)"
                : "inherit"
          }}
        />
      ) : (
        <MicFilled style={{ width: "24px", height: "24px" }} />
      ),
    [isMicMuted, canVoiceChat, micPermissionDenied, isMicEnabled, micUnavailableReason]
  );

  const notifyMicUnavailable = useUnavailableToolbarAction(micUnavailableReason);
  const onClick = event => {
    if (isMicStarting) {
      event?.preventDefault?.();
      return;
    }

    if (notifyMicUnavailable(event)) {
      return;
    }

    toggleMute();
  };

  const primaryActionButtonProps = {
    onClick,
    onFocus: prepareMic,
    onPointerDown: prepareMic,
    onPointerEnter: prepareMic,
    "aria-disabled": !!micUnavailableReason || isMicStarting,
    "aria-busy": isMicStarting
  };

  const micButtonDisabled = useMemo(() => !canVoiceChat || micPermissionDenied, [canVoiceChat, micPermissionDenied]);

  const [micChanges, setMicChanges] = useState(0);

  const updateMic = useCallback(level => {
    setMicChanges(level);
  }, []);

  useVolumeMeter({
    analyser: scene.systems["hubs-systems"].audioSystem.outboundAnalyser,
    update: updateMic
  });

  const { playSound } = useSound({
    scene,
    sound: SOUND_SPEAKER_TONE
  });

  const { onOpenChange: onOpenSettingsChange, onTabChange: onTabSettingsChange } = useSettingsContext();

  const onOpenSettings = () => {
    onOpenSettingsChange(true);
    onTabSettingsChange("audiovideo");
  };

  if (micPermissionDenied) {
    return (
      <Dialog>
        <DialogTrigger disableButtonEnhancement>
          {triggerProps => (
            <SplitButton
              primaryActionButton={primaryActionButtonProps}
              menuButton={triggerProps}
              appearance="subtle"
              className={
                micButtonDisabled ? mergeClasses(styles.buttonSplit, styles.buttonSplitDisabled) : styles.buttonSplit
              }
              icon={microphoneIcon}
              menuIcon={<InfoRegular style={{ width: "16px", height: "16px", marginBottom: "4px", color: "var(--color-text-primary)" }} />}
            />
          )}
        </DialogTrigger>
        <DialogSurface style={{ maxWidth: "480px" }}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger>
                  <Button appearance="transparent" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              <FormattedMessage
                id="mic-permission-denied-modal.title"
                defaultMessage="The metaverse is blocked from using your microphone"
              />
            </DialogTitle>
            <DialogContent style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <Body1 style={{ color: "var(--color-text-secondary)" }}>
                  <FormattedMessage
                    id="mic-permission-denied-modal.page-info-step"
                    defaultMessage="1. Click the page info icon on your browser address bar."
                  />
                </Body1>
                <Body1 style={{ color: "var(--color-text-secondary)" }}>
                  <FormattedMessage
                    id="mic-permission-denied-modal.permissions-step"
                    defaultMessage="2. Turn on the microphone permissions."
                  />
                </Body1>
              </div>
              <Image src={PermissionDenied} style={{ borderRadius: "8px" }} />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  }

  if (micUnavailableReason) {
    return (
      <Tooltip content={micUnavailableReason}>
        <SplitButton
          primaryActionButton={primaryActionButtonProps}
          menuButton={{
            onClick,
            "aria-label": micUnavailableReason
          }}
          appearance="subtle"
          className={mergeClasses(styles.buttonSplit, styles.buttonSplitDisabled)}
          icon={<MicOffFilled style={{ width: "24px", height: "24px", color: "var(--color-danger)" }} />}
          menuIcon={<InfoRegular style={{ width: "16px", height: "16px", marginBottom: "4px", color: "var(--color-text-primary)" }} />}
        />
      </Tooltip>
    );
  }

  return (
    <Popover positioning="below">
      <PopoverTrigger disableButtonEnhancement>
        {triggerProps => (
          <SplitButton
            menuButton={triggerProps}
            disabled={micButtonDisabled || isMicStarting}
            primaryActionButton={primaryActionButtonProps}
            appearance="subtle"
            className={
              micButtonDisabled ? mergeClasses(styles.buttonSplit, styles.buttonSplitDisabled) : styles.buttonSplit
            }
            icon={microphoneIcon}
            menuIcon={
              isMicStarting ? (
                <Spinner size="tiny" />
              ) : (
                <ChevronUpFilled style={{ width: "16px", height: "16px", marginBottom: "4px", color: "var(--color-text-primary)" }} />
              )
            }
          />
        )}
      </PopoverTrigger>
      <PopoverSurface
        style={{
          width: "450px",
          maxWidth: "100%",
          display: "flex",
          gap: "10px",
          alignItems: "center",
          padding: "8px 4px"
        }}
        className={styles.menuPopover}
      >
        <MicPopoverSelect
          positioning="above"
          name={"mic"}
          Icon={MicFilled}
          {...micDevices}
          onChange={micDeviceChanged}
          onClick={prepareMic}
        >
          <MenuItem className={styles.micProgressContainer} icon={<MicFilled />}>
            <ProgressBar className={styles.progressBarColor} value={micChanges} thickness="large" color="var(--color-success)" />
          </MenuItem>
        </MicPopoverSelect>

        <MicPopoverSelect
          positioning="above"
          name={"speaker"}
          Icon={Speaker220Filled}
          {...speakerDevices}
          onChange={speakerDeviceChanged}
        >
          <MenuItem
            persistOnClick={true}
            className={styles.menuListItem}
            onClick={playSound}
            icon={<Speaker220Filled />}
          >
            <FormattedMessage id="toolbar.test-speaker" defaultMessage="Test Speaker" />
          </MenuItem>
        </MicPopoverSelect>

        <Button onClick={onOpenSettings} appearance="transparent" icon={<Settings20Filled />} />
      </PopoverSurface>
    </Popover>
  );
}

ToolbarMic.propTypes = {
  styles: PropTypes.shape({
    buttonSplit: PropTypes.string,
    buttonSplitDisabled: PropTypes.string,
    menuPopover: PropTypes.string,
    micProgressContainer: PropTypes.string,
    progressBarColor: PropTypes.string,
    menuListItem: PropTypes.string
  }).isRequired,
  scene: PropTypes.shape({
    systems: PropTypes.shape({
      "hubs-systems": PropTypes.shape({
        audioSystem: PropTypes.shape({
          outboundAnalyser: PropTypes.object
        })
      })
    })
  }).isRequired
};
